import logger from './logger';

/**
 * Retry utility with exponential backoff
 * Useful for handling transient network errors
 */

export interface RetryOptions {
	maxAttempts?: number;
	initialDelay?: number;
	maxDelay?: number;
	backoffMultiplier?: number;
	shouldRetry?: (error: unknown) => boolean;
	onRetry?: (attempt: number, error: unknown) => void;
}

const defaultOptions: Required<RetryOptions> = {
	maxAttempts: 3,
	initialDelay: 1000, // 1 second
	maxDelay: 10000, // 10 seconds
	backoffMultiplier: 2,
	shouldRetry: (error: unknown) => {
		// Retry on network errors or 5xx server errors
		const err = error as { response?: { status: number }; status?: number };
		if (!err.response) return true; // Network error
		const status = err.response?.status || err.status;
		return status !== undefined && status >= 500 && status < 600; // Server errors
	},
	onRetry: () => {},
};

/**
 * Retry a function with exponential backoff
 * @param fn Function to retry
 * @param options Retry configuration
 * @returns Promise with the result of the function
 */
export async function retryWithBackoff<T>(
	fn: () => Promise<T>,
	options: RetryOptions = {},
): Promise<T> {
	const opts = { ...defaultOptions, ...options };
	let lastError: unknown;

	for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
		try {
			return await fn();
		} catch (error) {
			lastError = error;

			// Check if we should retry
			if (attempt === opts.maxAttempts || !opts.shouldRetry(error)) {
				throw error;
			}

			// Calculate delay with exponential backoff
			const delay = Math.min(
				opts.initialDelay * Math.pow(opts.backoffMultiplier, attempt - 1),
				opts.maxDelay,
			);

			logger.warn(`⚠️ Attempt ${attempt}/${opts.maxAttempts} failed, retrying in ${delay}ms...`, {
				error,
			});

			// Call onRetry callback
			opts.onRetry(attempt, error);

			// Wait before retrying
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}

	throw lastError;
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
	const err = error as { response?: unknown; message?: string; code?: string };
	return (
		!err.response ||
		err.message === 'Network Error' ||
		err.message === 'Failed to fetch' ||
		err.code === 'ECONNABORTED' ||
		err.code === 'ERR_NETWORK'
	);
}

/**
 * Check if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
	// Network errors are retryable
	if (isNetworkError(error)) return true;

	// 5xx server errors are retryable
	const err = error as { response?: { status: number }; status?: number };
	const status = err.response?.status || err.status;
	if (status !== undefined && status >= 500 && status < 600) return true;

	// 429 Too Many Requests is retryable
	if (status === 429) return true;

	// 408 Request Timeout is retryable
	if (status === 408) return true;

	return false;
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyErrorMessage(error: unknown): string {
	// Network errors
	if (isNetworkError(error)) {
		return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
	}

	// Server errors
	const err = error as {
		response?: { status: number; data?: { message?: string } };
		status?: number;
		message?: string;
	};
	const status = err.response?.status || err.status;
	if (status !== undefined && status >= 500 && status < 600) {
		return 'Serverfehler. Bitte versuchen Sie es später erneut.';
	}

	// Rate limiting
	if (status === 429) {
		return 'Zu viele Anfragen. Bitte warten Sie einen Moment und versuchen Sie es erneut.';
	}

	// Timeout
	if (status === 408) {
		return 'Die Anfrage hat zu lange gedauert. Bitte versuchen Sie es erneut.';
	}

	// Client errors
	if (status !== undefined && status >= 400 && status < 500) {
		return err.response?.data?.message || 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.';
	}

	// Generic error
	return err.message || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
}

// Note: localStorage-based retry functions removed
// We now use Pinia persistence in the wizard store instead
// This provides better type safety, reactivity, and integration
